import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import Login from "@/pages/Login";
import Marketing from "@/pages/Marketing";
import Pricing from "@/pages/Pricing";
import Signup from "@/pages/Signup";
import Legal from "@/pages/Legal";
import Contact from "@/pages/Contact";
import { EntitlementsProvider } from "@/context/EntitlementsContext";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import { Portfolios, Programmes } from "@/pages/Hierarchy";
import { AdminLists, AdminUsers, AdminBranding } from "@/pages/Admin";
import AdminApprovers from "@/pages/AdminApprovers";
import Reports from "@/pages/Reports";
import Resources from "@/pages/Resources";
import Assistant from "@/pages/Assistant";
import Controls from "@/pages/Controls";
import AdminPeriods from "@/pages/AdminPeriods";
import Approvals from "@/pages/Approvals";
import ProgrammeReport from "@/pages/ProgrammeReport";
import AdminBI from "@/pages/AdminBI";
import AdminScheduleTemplates from "@/pages/AdminScheduleTemplates";
import AdminFrameworks from "@/pages/AdminFrameworks";
import Compliance from "@/pages/Compliance";
import AdminSector from "@/pages/AdminSector";
import AdminDemoData from "@/pages/AdminDemoData";
import AdminBilling from "@/pages/AdminBilling";
import PaymentResult from "@/pages/PaymentResult";
import ClientDashboard from "@/pages/ClientDashboard";
import AdminAI from "@/pages/AdminAI";
import AdminAIEngine from "@/pages/AdminAIEngine";
import AdminAssurance from "@/pages/AdminAssurance";

const Protected = ({ children, roles }) => {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Loading…</div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "sponsor") return <Navigate to="/client" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

const Landing = () => {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Loading…</div>;
  if (!user) return <Marketing />;
  return <Navigate to={user.role === "sponsor" ? "/client" : "/dashboard"} replace />;
};

const ClientView = () => {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <ClientDashboard />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingProvider>
          <EntitlementsProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/contact" element={<Contact />} />

            <Route path="/legal/:slug" element={<Legal />} />
            <Route path="/privacy" element={<Legal />} />
            <Route path="/terms" element={<Legal />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/projects" element={<Protected><Projects /></Protected>} />
            <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
            <Route path="/portfolios" element={<Protected><Portfolios /></Protected>} />
            <Route path="/programmes" element={<Protected><Programmes /></Protected>} />
            <Route path="/programmes/:id/report" element={<Protected><ProgrammeReport /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/compliance" element={<Protected><Compliance /></Protected>} />
            <Route path="/resources" element={<Protected><Resources /></Protected>} />
            <Route path="/assistant" element={<Protected><Assistant /></Protected>} />
            <Route path="/controls/:kind" element={<Protected><Controls /></Protected>} />
            <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
            <Route path="/admin" element={<Protected roles={["admin", "pmo"]}><AdminLists /></Protected>} />
            <Route path="/admin/users" element={<Protected roles={["admin", "pmo"]}><AdminUsers /></Protected>} />
            <Route path="/admin/branding" element={<Protected roles={["admin", "pmo"]}><AdminBranding /></Protected>} />
            <Route path="/admin/approvers" element={<Protected roles={["admin", "pmo"]}><AdminApprovers /></Protected>} />
            <Route path="/admin/periods" element={<Protected roles={["admin", "pmo"]}><AdminPeriods /></Protected>} />
            <Route path="/admin/bi" element={<Protected roles={["admin", "pmo"]}><AdminBI /></Protected>} />
            <Route path="/admin/schedule-templates" element={<Protected roles={["admin", "pmo"]}><AdminScheduleTemplates /></Protected>} />
            <Route path="/admin/frameworks" element={<Protected roles={["admin", "pmo"]}><AdminFrameworks /></Protected>} />
            <Route path="/admin/sector" element={<Protected roles={["admin", "pmo"]}><AdminSector /></Protected>} />
            <Route path="/admin/demo-data" element={<Protected roles={["admin", "pmo"]}><AdminDemoData /></Protected>} />
            <Route path="/admin/billing" element={<Protected roles={["admin", "pmo"]}><AdminBilling /></Protected>} />
            <Route path="/payment/success" element={<Protected><PaymentResult /></Protected>} />
            <Route path="/payment/cancel" element={<Protected><PaymentResult cancelled /></Protected>} />
            <Route path="/admin/ai-engine" element={<Protected roles={["admin", "pmo"]}><AdminAIEngine /></Protected>} />
            <Route path="/admin/ai-assurance" element={<Protected roles={["admin", "pmo"]}><AdminAssurance /></Protected>} />
            <Route path="/admin/ai" element={<Protected roles={["admin", "pmo"]}><AdminAI /></Protected>} />
            <Route path="/client" element={<ClientView />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </EntitlementsProvider>
        </BrandingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
